import { Link } from '@tanstack/react-router';
import { Lock } from 'lucide-react';
import { useCampaignStore } from '@/stores';
import { useMayManageCampaign } from '@/hooks';

/**
 * Wraps a paywalled feature. When subscription is Unpaid/Canceled, blurs the content. A member who
 * may manage billing gets a link to the hub's Billing sub-page (paywall-exempt via the
 * `/app/settings` prefix, so a lapsed campaign can still reach it to pay); anyone else is told to
 * ask the campaign owner, since the billing page would only tell them they can't pay.
 * Renders children normally for Active/Trialing/PastDue.
 */
export function PaywallOverlay({ children }: { children: React.ReactNode }) {
  const status = useCampaignStore((s) => s.selectedCampaign?.subscriptionStatus);
  const isLocked = status === 'Unpaid' || status === 'Canceled';
  const mayManageBilling = useMayManageCampaign();

  if (!isLocked) return <>{children}</>;

  return (
    <div className="relative">
      <div className="pointer-events-none select-none blur-sm opacity-50">
        {children}
      </div>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg border p-6 max-w-sm text-center space-y-3">
          <Lock className="h-8 w-8 mx-auto text-muted-foreground" />
          <h3 className="font-semibold text-gray-900">Subscription Required</h3>
          <p className="text-sm text-muted-foreground">
            {status === 'Canceled'
              ? 'Your subscription has been canceled.'
              : 'Your free trial has ended.'}
            {' '}
            {mayManageBilling
              ? 'Subscribe to access this feature.'
              : 'Ask your campaign owner to subscribe so you can use this feature.'}
          </p>
          {mayManageBilling && (
            <Link
              to="/app/settings/billing"
              className="inline-block text-sm font-medium text-blue-600 hover:text-blue-800 underline"
            >
              Go to Billing Settings
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
